import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, mergeProps as _mergeProps, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = { class: "space-x-2 text-center" }
const _hoisted_3 = { class: "flex justify-center text-sm text-gray-600" }
const _hoisted_4 = {
  for: "file-upload",
  class: "relative font-medium text-gray-900 bg-white rounded-md cursor-pointer hover:text-gray-900 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-gray-900"
}
const _hoisted_5 = { class: "font-bold" }
const _hoisted_6 = ["id", "name"]
const _hoisted_7 = { class: "mt-1 text-sm text-gray-500" }
const _hoisted_8 = {
  key: 1,
  class: "w-full mt-2 space-y-2"
}
const _hoisted_9 = {
  key: 2,
  class: "w-full mt-2 space-y-2"
}

import { ref, computed } from "vue";
// import { usePage } from "@inertiajs/vue3";
import { useDropzone } from "vue3-dropzone";
import FilePreview from "./FilePreview.vue";

export default /*@__PURE__*/_defineComponent({
  __name: 'DropzoneInput',
  props: {
    path: {
        type: String,
        default: "",
    },
    file: {
        type: String,
    },
    error: {
        type: String,
        default: null,
    },
    description: {
        type: String,
        default: "Max File Size: 2MB · File Types: JPG, PNG",
    },
    name: {
        type: String,
        default: null,
    },
    id: {
        type: String,
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    // Dropzone props
    multiple: {
        type: Boolean,
        default: false,
    },
    maxSize: {
        type: Number,
        default: 10,
    },
    maxFiles: {
        type: Number,
        default: 0,
    },
    accept: {
        type: String,
        default: "image/*, video/*, application/pdf",
    },
    overwritePath: {
        type: Boolean,
        default: false,
    },
    previewFile: {
        type: Boolean,
        default: true,
    },
    extSource: {
        type: String,
        default: null,
    },
    extFileType: {
        type: String,
        default: null,
    },
    extFileName: {
        type: String,
        default: null,
    },
    extFileSize: {
        type: String,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    allowDelete: {
        type: Boolean,
        default: true,
    },
},
  emits: ['update:path', 'update:file', 'delete:file'],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit

const rejectErrors = ref([]);
const acceptedFileSrcs = ref([]);

// Triggers when file is dropped or selected
function onDrop(acceptFiles, rejectReasons) {
    // console.log(acceptFiles);
    // console.log(rejectReasons);
    // process errors
    rejectErrors.value = rejectReasons.length
        ? renderErrors(rejectReasons)
        : [];

    // process SRCs
    acceptedFileSrcs.value = [];
    if (acceptFiles.length) {
        acceptFiles.forEach((file) => {
            processFileSrc(file).then((base64) => {
                acceptedFileSrcs.value.push(base64);
                if (props.overwritePath) {
                    emit("update:path", base64);
                }
            });
        });
        // emit v-model change event
        const payload = props.multiple
            ? acceptedFiles.value
            : acceptedFiles.value[0];
        emit("update:file", payload);
    } else {
        emit("update:file", null);
    }
}

function processFileSrc(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
}

function renderErrors(rejectReasons) {
    const errors = rejectReasons.map((reason) => {
        return {
            filename: reason.file.name,
            errors: reason.errors.map((error) => {
                switch (error.code) {
                    case "file-invalid-type":
                        return "Invalid file type";
                    case "file-too-large":
                        return `File too large (${reason.file.size / 1000 / 1000} MB)`;
                }
            }),
        };
    });
    return props.multiple ? errors : [errors[0]];
}

const { getRootProps, getInputProps, isDragActive, acceptedFiles } =
    useDropzone({
        onDrop,
        maxSize: props.maxSize * 1000 * 1000, // convert to MB
        maxFiles: props.maxFiles,
        multiple: props.multiple,
        accept: props.accept,
    });

// displayed image
const storageUrl = computed(() => process.env.VUE_APP_API_STORAGE_URL);
const displaySrc = computed(() => {
    if (acceptedFileSrcs.value.length) {
        return acceptedFileSrcs.value[0];
    } else if (props.extSource) {
        return props.extSource;
    } else if (props.path) {
        return storageUrl.value + props.path;
    }
    return null;
});

function fileDownload(key) {
    const a = document.createElement("a");
    a.href = acceptedFileSrcs.value[key];
    a.download = acceptedFiles.value[key].name;
    a.target = "_blank";
    a.click();
    a.remove();
}

function fileDelete(key) {
    acceptedFiles.value.splice(key, 1);
    acceptedFileSrcs.value.splice(key, 1);

    // emit v-model change event
    const payload = props.multiple
        ? acceptedFiles.value
        : acceptedFiles.value[0];
    emit("update:file", payload);
    emit("delete:file", payload);
}

function previewFileDownload(item) {
    if(props.extSource) {
        downloadPdf();
    } else {
        const a = document.createElement("a");
        a.href = item;
        a.target = "_blank";
        a.click();
        a.remove();
    }
}

const previewFileDelete = (item) => {
    if (props.extSource) {
        emit("delete:file", item);
        emit("update:path", null);
    } else {
        // If the file is not external, we can just clear the path
        emit("update:path", null);
        emit("update:file", null);
    }
}

const downloadPdf = () => {
    if (props.extSource) {
        const base64Data = props.extSource;
        // Check if it's a base64 string
        let fileAttachment = null;
        if (typeof base64Data === 'string' && base64Data.startsWith('data:')) {
            // Get the MIME type from the base64 string
            const mimeType = base64Data.split(';')[0].split(':')[1];
            // Convert base64 to blob
            const byteCharacters = atob(base64Data.split(',')[1]);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });
            
            // Create File object from Blob
            fileAttachment = new File([blob], props.extFileName, { type: mimeType });
        }
        
        const link = document.createElement('a');
        link.href = URL.createObjectURL(fileAttachment);
        link.download = `${props.extFileName}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
    } else {
        console.error('PDF data not available');
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("label", {
      for: __props.id,
      class: "block mb-1 text-sm text-gray-900"
    }, _toDisplayString(__props.label), 9, _hoisted_1),
    (!__props.disabled)
      ? (_openBlock(), _createElementBlock("div", _mergeProps({
          key: 0,
          class: ["flex justify-center w-full px-6 py-6 border-2 border-gray-300 border-dashed rounded-md", _unref(isDragActive) ? 'border-gray-900' : 'border-gray-300']
        }, _unref(getRootProps)()), [
          _createElementVNode("div", _hoisted_2, [
            (_openBlock(), _createElementBlock("svg", {
              class: _normalizeClass(["mx-auto", _unref(isDragActive) ? 'text-gray-900' : 'text-gray-400']),
              width: "48",
              height: "48",
              viewBox: "0 0 48 48",
              fill: "none",
              xmlns: "http://www.w3.org/2000/svg"
            }, [...(_cache[2] || (_cache[2] = [
              _createElementVNode("path", {
                "fill-rule": "evenodd",
                "clip-rule": "evenodd",
                d: "M19.7143 11.2857C18.0093 11.2857 16.3742 11.963 15.1686 13.1686C13.963 14.3742 13.2857 16.0093 13.2857 17.7143V26.2857C13.2857 29.1273 14.4145 31.8525 16.4239 33.8619C18.4332 35.8712 21.1584 37 24 37C26.8416 37 29.5668 35.8712 31.5761 33.8619C33.5855 31.8525 34.7143 29.1273 34.7143 26.2857V17.7143C34.7143 17.146 34.9401 16.6009 35.3419 16.1991C35.7438 15.7972 36.2888 15.5714 36.8571 15.5714C37.4255 15.5714 37.9705 15.7972 38.3724 16.1991C38.7742 16.6009 39 17.146 39 17.7143V26.2857C39 28.2555 38.612 30.2061 37.8582 32.026C37.1044 33.8458 35.9995 35.4994 34.6066 36.8923C33.2137 38.2852 31.5601 39.3901 29.7403 40.1439C27.9204 40.8977 25.9698 41.2857 24 41.2857C22.0302 41.2857 20.0796 40.8977 18.2597 40.1439C16.4399 39.3901 14.7863 38.2852 13.3934 36.8923C12.0005 35.4994 10.8956 33.8458 10.1418 32.026C9.38799 30.2061 9 28.2555 9 26.2857V17.7143C9 14.8727 10.1288 12.1475 12.1381 10.1381C14.1475 8.12882 16.8727 7 19.7143 7C22.5559 7 25.2811 8.12882 27.2904 10.1381C29.2997 12.1475 30.4286 14.8727 30.4286 17.7143V26.2857C30.4286 27.9907 29.7513 29.6258 28.5457 30.8314C27.3401 32.037 25.705 32.7143 24 32.7143C22.295 32.7143 20.6599 32.037 19.4543 30.8314C18.2487 29.6258 17.5714 27.9907 17.5714 26.2857V17.7143C17.5714 17.146 17.7972 16.6009 18.1991 16.1991C18.6009 15.7972 19.146 15.5714 19.7143 15.5714C20.2826 15.5714 20.8277 15.7972 21.2295 16.1991C21.6314 16.6009 21.8571 17.146 21.8571 17.7143V26.2857C21.8571 26.854 22.0829 27.3991 22.4848 27.8009C22.8866 28.2028 23.4317 28.4286 24 28.4286C24.5683 28.4286 25.1134 28.2028 25.5152 27.8009C25.9171 27.3991 26.1429 26.854 26.1429 26.2857V17.7143C26.1429 16.0093 25.4656 14.3742 24.26 13.1686C23.0544 11.963 21.4192 11.2857 19.7143 11.2857Z",
                fill: "#6B7280"
              }, null, -1)
            ]))], 2)),
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("label", _hoisted_4, [
                _createElementVNode("span", _hoisted_5, "Select " + _toDisplayString(__props.multiple ? "files" : "a file"), 1),
                _cache[3] || (_cache[3] = _createElementVNode("span", { class: "pl-1 text-gray-500" }, "or drag and drop", -1)),
                _createElementVNode("input", _mergeProps(_unref(getInputProps)(), {
                  id: __props.id,
                  name: __props.name,
                  type: "file",
                  class: "sr-only"
                }), null, 16, _hoisted_6)
              ])
            ]),
            _createElementVNode("p", _hoisted_7, _toDisplayString(__props.description), 1)
          ])
        ], 16))
      : _createCommentVNode("", true),
    (__props.previewFile && _unref(acceptedFiles).length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(acceptedFiles), (file, key) => {
            return (_openBlock(), _createBlock(FilePreview, {
              key: file,
              "file-type": file.type,
              source: acceptedFileSrcs.value[key],
              "file-name": file.name,
              size: file.size,
              "onFile:download": ($event: any) => (fileDownload(key)),
              "onFile:delete": ($event: any) => (fileDelete(key)),
              allowDelete: __props.allowDelete
            }, null, 8, ["file-type", "source", "file-name", "size", "onFile:download", "onFile:delete", "allowDelete"]))
          }), 128))
        ]))
      : (__props.path)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createVNode(FilePreview, {
              source: displaySrc.value,
              allowDelete: __props.allowDelete,
              "onFile:download": _cache[0] || (_cache[0] = ($event: any) => (previewFileDownload(displaySrc.value))),
              "onFile:delete": _cache[1] || (_cache[1] = ($event: any) => (previewFileDelete(displaySrc.value))),
              fileType: __props.extFileType,
              fileName: __props.extFileName,
              size: __props.extFileSize
            }, null, 8, ["source", "allowDelete", "fileType", "fileName", "size"])
          ]))
        : _createCommentVNode("", true),
    (__props.error)
      ? (_openBlock(), _createElementBlock("p", {
          key: 3,
          class: _normalizeClass(["mt-2 text-sm", __props.error ? 'text-red-600' : 'text-gray-800']),
          id: "email-error"
        }, _toDisplayString(__props.error), 3))
      : _createCommentVNode("", true),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rejectErrors.value, (rejectError, index) => {
      return (_openBlock(), _createElementBlock("p", {
        key: index,
        class: "mt-2 text-sm text-red-600"
      }, _toDisplayString(rejectError.filename) + ": " + _toDisplayString(rejectError.errors.join(", ")), 1))
    }), 128))
  ]))
}
}

})